import {
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useState,
  useCallback,
} from 'react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'top-center'
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center';

export interface ToastProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  id?: string;
  variant?: ToastVariant;
  title?: string;
  message: string;
  duration?: number; // 0 = no auto-dismiss
  onClose?: () => void;
  showCloseButton?: boolean;
  icon?: ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const variantStyles: Record<ToastVariant, string> = {
  success: 'bg-green-50 border-green-500 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  error: 'bg-red-50 border-red-500 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  warning: 'bg-yellow-50 border-yellow-500 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
  info: 'bg-blue-50 border-blue-500 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
};

const variantIconColors: Record<ToastVariant, string> = {
  success: 'text-green-500',
  error: 'text-red-500',
  warning: 'text-yellow-500',
  info: 'text-blue-500',
};

const variantProgressColors: Record<ToastVariant, string> = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-yellow-500',
  info: 'bg-blue-500',
};

// Icons for each variant
const SuccessIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ErrorIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

const WarningIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const InfoIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const defaultIcons: Record<ToastVariant, ReactNode> = {
  success: <SuccessIcon />,
  error: <ErrorIcon />,
  warning: <WarningIcon />,
  info: <InfoIcon />,
};

export const Toast = forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      id: _id,
      variant = 'info',
      title,
      message,
      duration = 5000,
      onClose,
      showCloseButton = true,
      icon,
      action,
      className = '',
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isExiting, setIsExiting] = useState(false);
    const [progress, setProgress] = useState(100);

    const handleClose = useCallback(() => {
      setIsExiting(true);
      setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, 300); // Match animation duration
    }, [onClose]);

    // Auto-dismiss timer
    useEffect(() => {
      if (duration <= 0) return;

      const startTime = Date.now();
      const endTime = startTime + duration;

      const progressInterval = setInterval(() => {
        const now = Date.now();
        const remaining = endTime - now;
        const progressPercent = (remaining / duration) * 100;

        if (progressPercent <= 0) {
          clearInterval(progressInterval);
          handleClose();
        } else {
          setProgress(progressPercent);
        }
      }, 50);

      return () => clearInterval(progressInterval);
    }, [duration, handleClose]);

    if (!isVisible) return null;

    const displayIcon = icon || defaultIcons[variant];

    return (
      <div
        ref={ref}
        role="alert"
        aria-live="polite"
        className={`
          relative overflow-hidden
          w-full max-w-sm
          bg-white dark:bg-gray-800 rounded-lg shadow-lg
          border-l-4 ${variantStyles[variant]}
          transform transition-all duration-300 ease-in-out
          ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}
          ${className}
        `.trim()}
        {...props}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            {displayIcon && (
              <div className={`flex-shrink-0 ${variantIconColors[variant]}`}>
                {displayIcon}
              </div>
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
              {title && (
                <p className="text-sm font-semibold mb-1">{title}</p>
              )}
              <p className="text-sm opacity-90">{message}</p>

              {/* Action button */}
              {action && (
                <button
                  type="button"
                  onClick={() => {
                    action.onClick();
                    handleClose();
                  }}
                  className={`
                    mt-2 text-sm font-medium underline
                    hover:opacity-80 transition-opacity
                    ${variantIconColors[variant]}
                  `}
                >
                  {action.label}
                </button>
              )}
            </div>

            {/* Close button */}
            {showCloseButton && (
              <button
                type="button"
                onClick={handleClose}
                className="flex-shrink-0 p-1 rounded hover:bg-black/5 transition-colors"
                aria-label="Cerrar notificacion"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>

        {/* Progress bar for auto-dismiss */}
        {duration > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/5">
            <div
              className={`h-full transition-all duration-50 ${variantProgressColors[variant]}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    );
  }
);

Toast.displayName = 'Toast';

// Convenience components for common toast patterns
export interface QuickToastProps {
  message: string;
  title?: string;
  duration?: number;
  onClose?: () => void;
  action?: ToastProps['action'];
}

export const SuccessToast = (props: QuickToastProps) => (
  <Toast variant="success" {...props} />
);

export const ErrorToast = (props: QuickToastProps) => (
  <Toast variant="error" {...props} />
);

export const WarningToast = (props: QuickToastProps) => (
  <Toast variant="warning" {...props} />
);

export const InfoToast = (props: QuickToastProps) => (
  <Toast variant="info" {...props} />
);

export default Toast;
